/**
 * BOM Column Mapper
 * 
 * Smart column detection and mapping for piping BOM tables.
 * Pre-configured for common BOM formats used in piping isometrics.
 */

// Predefined BOM formats
export const BOM_FORMATS = {
  PIPING_ISO: {
    name: 'Piping Isometric BOM',
    description: 'Standard format for piping isometric drawings',
    columns: [
      { key: 'iso_sheet_number', label: 'ISO Sheet Number', patterns: [/^iso/i, /^sheet/i, /^dwg/i, /^drawing/i] },
      { key: 'type', label: 'Type', patterns: [/^type/i, /^item/i, /^component/i] },
      { key: 'material', label: 'Material', patterns: [/^mat/i, /^material/i] },
      { key: 'schedule', label: 'Schedule', patterns: [/^sch/i, /^schedule/i, /^wall/i] },
      { key: 'size', label: 'Size', patterns: [/^size/i, /^dia/i, /^nominal/i, /^nps/i, /^dn/i] },
      { key: 'item_code', label: 'Item Code', patterns: [/^item.*code/i, /^part.*no/i, /^p\/n/i, /^code/i] },
      { key: 'quantity', label: 'Quantity', patterns: [/^qty/i, /^quantity/i, /^count/i] },
      { key: 'description', label: 'Description', patterns: [/^desc/i, /^other/i, /^info/i, /^remark/i, /^note/i] }
    ]
  },
  GENERAL_BOM: {
    name: 'General BOM',
    description: 'Generic Bill of Materials format',
    columns: [
      { key: 'part_number', label: 'Part Number', patterns: [/^part/i, /^p\/n/i, /^item.*no/i] },
      { key: 'description', label: 'Description', patterns: [/^desc/i, /^name/i] },
      { key: 'quantity', label: 'Quantity', patterns: [/^qty/i, /^quantity/i] },
      { key: 'unit', label: 'Unit', patterns: [/^unit/i, /^uom/i] },
      { key: 'material', label: 'Material', patterns: [/^mat/i, /^material/i] },
      { key: 'remarks', label: 'Remarks', patterns: [/^remark/i, /^note/i, /^comment/i] }
    ]
  }
};

/**
 * Auto-detect column types from header row
 * @param {string[]} headers - Array of header strings
 * @param {string} format - Format key from BOM_FORMATS
 * @returns {Object[]} - Array of column mappings
 */
export function detectColumnTypes(headers, format = 'PIPING_ISO') {
  const formatConfig = BOM_FORMATS[format] || BOM_FORMATS.PIPING_ISO;
  const mappings = [];
  
  headers.forEach((header, index) => {
    const normalizedHeader = header.toLowerCase().trim();
    
    // Try to match against known patterns
    let matched = false;
    for (const col of formatConfig.columns) {
      for (const pattern of col.patterns) {
        if (pattern.test(normalizedHeader)) {
          mappings.push({
            index,
            key: col.key,
            label: col.label,
            originalHeader: header
          });
          matched = true;
          break;
        }
      }
      if (matched) break;
    }
    
    // If no match, use generic column name
    if (!matched) {
      mappings.push({
        index,
        key: `column_${index + 1}`,
        label: header || `Column ${index + 1}`,
        originalHeader: header
      });
    }
  });
  
  return mappings;
}

/**
 * Apply column mapping to raw table data
 * @param {Object} tableData - Table data with headers and rows
 * @param {Object[]} mappings - Column mappings from detectColumnTypes
 * @returns {Object[]} - Array of mapped row objects
 */
export function applyColumnMapping(tableData, mappings) {
  const { rows } = tableData;
  
  return rows.map((row, rowIndex) => {
    const mappedRow = { _row_index: rowIndex + 1 };
    
    mappings.forEach(mapping => {
      if (mapping.index < row.length) {
        const value = row[mapping.index];
        mappedRow[mapping.key] = normalizeValue(value, mapping.key);
      }
    });
    
    return mappedRow;
  });
}

/**
 * Normalize a value based on its column type
 */
function normalizeValue(value, columnKey) {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  
  const trimmed = String(value).trim();
  
  switch (columnKey) {
    case 'quantity':
      // Extract numeric value
      const num = parseFloat(trimmed.replace(/[^0-9.-]/g, ''));
      return isNaN(num) ? trimmed : num;
      
    case 'schedule':
      // Normalize schedule format (S-10S, SCH 40, etc.)
      return trimmed.toUpperCase();
      
    case 'material':
      // Common material abbreviations
      // Keep original but could expand abbreviation if needed
      return trimmed;
      
    case 'size':
      // Keep size as-is but ensure it's a string
      return trimmed;
      
    default:
      return trimmed;
  }
}

/**
 * Parse raw text lines into table structure using column positions
 * @param {string[]} lines - Array of text lines
 * @param {Object} options - Parsing options
 * @returns {Object} - Table data with headers and rows
 */
export function parseTextToTable(lines, options = {}) {
  const {
    delimiter = null, // Auto-detect if null
    hasHeader = true,
    minColumns = 3
  } = options;
  
  if (lines.length === 0) {
    return { headers: [], rows: [] };
  }
  
  // Try to detect delimiter
  const detectedDelimiter = delimiter || detectDelimiter(lines[0]);
  
  // Parse all lines
  const parsedLines = lines.map(line => {
    if (detectedDelimiter) {
      return line.split(detectedDelimiter).map(cell => cell.trim());
    } else {
      // Split by multiple spaces
      return line.split(/\s{2,}/).map(cell => cell.trim()).filter(cell => cell);
    }
  });
  
  // Filter out lines with too few columns
  const validLines = parsedLines.filter(line => line.length >= minColumns);
  
  if (validLines.length === 0) {
    return { headers: [], rows: [] };
  }
  
  // First line is header if hasHeader is true
  const headers = hasHeader ? validLines[0] : validLines[0].map((_, i) => `Column ${i + 1}`);
  const rows = hasHeader ? validLines.slice(1) : validLines;
  
  return { headers, rows };
}

/**
 * Detect delimiter used in a text line
 */
function detectDelimiter(line) {
  const delimiters = [
    { char: '\t', count: (line.match(/\t/g) || []).length },
    { char: '|', count: (line.match(/\|/g) || []).length },
    { char: ';', count: (line.match(/;/g) || []).length },
    { char: ',', count: (line.match(/,/g) || []).length }
  ];
  
  // Find delimiter with most occurrences (min 2)
  const best = delimiters.sort((a, b) => b.count - a.count)[0];
  return best.count >= 2 ? best.char : null;
}

/**
 * Full pipeline: parse text and apply smart column mapping
 * @param {string} rawText - Raw text from PDF or OCR
 * @param {string} format - BOM format to use
 * @returns {Object} - Structured BOM data
 */
export function smartParse(rawText, format = 'PIPING_ISO') {
  // Split into lines
  const lines = rawText.split('\n').filter(line => line.trim());
  
  // Parse to table
  const tableData = parseTextToTable(lines);
  
  if (tableData.headers.length === 0) {
    return {
      success: false,
      error: 'Could not detect table structure',
      items: []
    };
  }
  
  // Detect column types
  const mappings = detectColumnTypes(tableData.headers, format);
  
  // Apply mapping
  const items = applyColumnMapping(tableData, mappings);
  
  return {
    success: true,
    items,
    headers: mappings.map(m => m.key),
    headerLabels: mappings.map(m => m.label),
    originalHeaders: tableData.headers,
    confidence: 75 // Rule-based parsing is moderately confident
  };
}

const bomColumnMapper = {
  BOM_FORMATS,
  detectColumnTypes,
  applyColumnMapping,
  parseTextToTable,
  smartParse
};

export default bomColumnMapper;
